import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Checkbox, FormControlLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { cookies, isStrictlyNecessary } from '../cookies';
import VendorCheckbox from '../VendorCheckbox';
import { useTCString, vendors } from '../iab-tcf';

const useStyles = makeStyles(theme => ({
  cookieOption: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
  },
  cookieDescription: {
    margin: 0,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
}));

/**
 * Customize consent for:
 * - MR cookie categories IDs
 * - IAB vendor IDs (by cookie ID)
 */
function CookiesVendorsTab({ selectedCookies, setSelectedCookies }) {
  const classes = useStyles();
  const { refreshTCString } = useTCString();

  // toggle cookie and it's related IAB vendors
  const toggleCookie = cookieKey => {
    // do not edit, should always be true
    if (isStrictlyNecessary(cookieKey)) {
      return;
    }

    setSelectedCookies(prev => {
      const newValue = !prev[cookieKey];

      // update IAB vendors related to the cookie
      vendors.setVendorsConsentByCookie(cookieKey, newValue);

      // updated cookie value
      return { ...prev, [cookieKey]: newValue };
    });
  };

  // toggle single IAB vendor, keeping cookie value unchanged for e2e test flexibility
  const toggleVendor = ({ vendorId }) => {
    let currentConsent = vendors.getVendorConsent(vendorId);
    vendors.setVendorConsent(vendorId, !currentConsent);
    refreshTCString();
  };

  return (
    <Fragment>
      {cookies.map(cookie => {
        return (
          <Box className={classes.cookieOption} key={cookie.value}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCookies[cookie.value]}
                  disabled={isStrictlyNecessary(cookie.value)}
                  data-test-id={`checkbox-cookie-${cookie.value}`}
                  onChange={() => toggleCookie(cookie.value)}
                  style={{
                    cursor: isStrictlyNecessary(cookie.value) ? 'default' : 'pointer',
                  }}
                />
              }
              label={
                <Typography component="span" variant="body1">
                  {cookie.label}
                </Typography>
              }
            />
            <Typography variant="body2" className={classes.cookieDescription}>
              {cookie.description}
            </Typography>

            {/* Show IAB vendors */}
            {vendors.CONSENT_IAB_VENDOR_MAP[cookie.value].length > 0 && (
              <Box m={2} ml={2}>
                <Typography variant="body1" gutterBottom>
                  Supported Vendors
                </Typography>
                {vendors.CONSENT_IAB_VENDOR_MAP[cookie.value].map(vendorId => (
                  <VendorCheckbox
                    key={vendorId}
                    vendorId={vendorId}
                    cookieKey={cookie.value}
                    isChecked={vendors.getVendorConsent(vendorId)}
                    onChange={toggleVendor}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Fragment>
  );
}

CookiesVendorsTab.propTypes = {
  selectedCookies: PropTypes.object.isRequired,
  setSelectedCookies: PropTypes.func.isRequired,
};

export default CookiesVendorsTab;
